import { Motion } from "@legendapp/motion";
import React, { memo } from "react";

import PlayerCardBaseContainer from "../PlayerCardBase";

import { Box } from "@/components/atoms/Box";

// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: @dnd-kit transform (CSS.Translate.toString(transform)) plus computed opacity/visibility/cursor during drag, AND spreads…
import { Box as UIBox } from "@/components/ui/box";
import UnifiedTagModal from "@/features/player-kanban/components/organisms/UnifiedTagModal";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 *
 */
interface KanbanPlayerCardViewProps {
  playerId: string;
  playerName: string;
  rank?: number;
  daysInStage?: number | null;
  isStale?: boolean;
  /** SE-5492: canonical player image URL; null for every player today (SE-5538). */
  imageUrl?: string | null;
  /** SE-5492: explicit position, distinct from the role archetype. */
  position?: string;
  /** SE-5492: total scout reports attached to the player. */
  reportsCount?: number;
  /** SE-5492: latest report's overall score, or null when unscored. */
  verdict?: string | null;
  archetype: string;
  age: number;
  gpr: number;
  contractValue: string;
  contractMonths: number | null;
  agentName: string;
  teamName: string;
  playerAgent?: string;
  /** SE-6490: the backing card's `updatedAt`, driving the footer. */
  updatedAt?: string | null;
  /** SE-6866: the resolved last-editor name completing the footer; null hides it. */
  editorName?: string | null;
  displayedTags: { name: string; color: string }[];
  currentTagIds: string[];
  expandedTagIndex: number | null;
  showAllTags: boolean;
  hasMoreTags: boolean;
  isHovered: boolean;
  showMoreTags: boolean;
  showTagSelector: boolean;
  isOptimistic?: boolean;
  boardId?: string;
  /**
   * Everything that attaches the drag node: React 19's `ref` (dnd-kit's
   * callback ref for the rank-reorder wrapper's sortable node when a rank drag
   * is live, otherwise the card's own cross-column draggable), plus that node's
   * listeners and accessibility attributes. Assembled by the Container
   * (SE-8019).
   *
   * The ref rides INSIDE this object rather than arriving as its own prop:
   * `react-hooks/refs` reads `ref={props.x}` — a member expression in `ref`
   * position — as accessing a ref during render.
   */
  dragBindings: Record<string, unknown>;
  /** The computed drag style for whichever mode is live. */
  dragStyle: React.CSSProperties;
  /**
   * Whether a RANK reorder is what this card is bound to.
   *
   * SE-6554: when it is, the whole card sits inside the drag-handle node, so a
   * drag begun anywhere on the card carries the cross-stage capability
   * SE-6556's gate resolves through that test id.
   */
  isRankSortable: boolean;
  onPress: () => void;
  onAddTag?: () => void;
  onRemoveTag?: (tagName: string) => void;
  onToggleTagExpansion: (index: number) => void;
  onToggleShowAllTags: () => void;
  onToggleShowMore: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
  onCloseTagSelector: () => void;
  onTagsChanged: (tagIds: string[]) => void;
  onPreventDrag?: () => void;
  showRemoveButtons?: boolean;
  bioData?: {
    customStats?: Record<string, unknown> | null;
    minGeminiPlayerValuation?: number | null;
    maxGeminiPlayerValuation?: number | null;
    [key: string]: unknown;
  };
}

/**
 * The card's face, through the shared base.
 *
 * A helper rather than a local const: the rank-sortable path wraps it in the
 * drag-handle node and the cross-column path renders it bare, so both branches
 * need the same body and a View may not hold statements (SE-8019).
 * @param props - View props.
 * @returns The card base.
 */
const renderCardBase = (
  props: KanbanPlayerCardViewProps
): React.ReactElement => (
  <PlayerCardBaseContainer
    playerId={props.playerId}
    playerName={props.playerName}
    rank={props.rank}
    daysInStage={props.daysInStage}
    isStale={props.isStale}
    imageUrl={props.imageUrl}
    position={props.position}
    reportsCount={props.reportsCount}
    verdict={props.verdict}
    archetype={props.archetype}
    age={props.age}
    gpr={props.gpr}
    contractValue={props.contractValue}
    contractMonths={props.contractMonths}
    agentName={props.agentName}
    teamName={props.teamName}
    playerAgent={props.playerAgent}
    updatedAt={props.updatedAt}
    editorName={props.editorName}
    displayedTags={props.displayedTags}
    expandedTagIndex={props.expandedTagIndex}
    showAllTags={props.showAllTags}
    hasMoreTags={props.hasMoreTags}
    isHovered={props.isHovered}
    showMoreTags={props.showMoreTags}
    isKanban
    onPress={props.onPress}
    onAddTag={props.onAddTag}
    onRemoveTag={props.onRemoveTag}
    onToggleTagExpansion={props.onToggleTagExpansion}
    onToggleShowAllTags={props.onToggleShowAllTags}
    onToggleShowMore={props.onToggleShowMore}
    onHoverIn={props.onHoverIn}
    onHoverOut={props.onHoverOut}
    onPreventDrag={props.onPreventDrag}
    onDragStart={undefined} // Disable custom drag - @dnd-kit handles this
    onDragEnd={undefined} // Disable custom drag - @dnd-kit handles this
    showRemoveButtons={props.showRemoveButtons}
    bioData={props.bioData}
  />
);

/**
 * Presentational component for draggable kanban player card with dnd-kit integration.
 * Renders player card with drag handles, animations, and tag modal.
 * @param props - Component props
 */
const KanbanPlayerCardView = (props: KanbanPlayerCardViewProps) => (
  <>
    <Motion.View
      // testID (not a data-testid spread): the underlying RN-web View
      // filters unknown data-* props, so only testID reaches the DOM.
      testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD}
      initial={props.isOptimistic ? { opacity: 0, scale: 0.95 } : undefined}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", damping: 20, stiffness: 300 }}
    >
      {/* design-system: dynamic-style waiver — data-driven value (dnd-kit transform + aria/role drag attributes) */}
      <UIBox
        {...{ "data-testid": TEST_ID_PLAYER_KANBAN.PLAYER_CARD_DRAG_HANDLE }}
        // eslint-disable-next-line no-restricted-syntax -- dnd-kit drag node: runtime CSS.Translate/Transform.toString(transform) + computed opacity/visibility/cursor/transition; not atomizable
        style={props.dragStyle}
        {...props.dragBindings}
      >
        {props.isRankSortable ? (
          /*
           * SE-6554: THE WHOLE CARD IS THE DRAG SURFACE (Raniel, 2026-08-06).
           * The 6-dot grip is gone and its drag role moved to the card body.
           *
           * The handle NODE stays and now wraps the entire card, which is what
           * makes that true: SE-6556's cross-stage gate resolves this exact
           * test id with `closest()` on the drag's activation target (see
           * `isRankHandleActivation` in MainContainer), so with the card inside
           * it, a drag begun anywhere on the card carries the full capability.
           * Delete the node instead and a rank-sorted card could only ever
           * reorder inside its own column — SE-5483's cross-column rank drop
           * would be unreachable, with no visible handle left to grab.
           *
           * Coexistence this preserves: a CLICK still opens the player,
           * because the PointerSensor only activates after a 3px distance
           * constraint (MainContainer); rank reorder still routes through the
           * sortable within a rank-sorted column; and `isDragActivationDisabled`
           * still gates members out of dragging at all.
           *
           * CONSEQUENCE, deliberate: SE-6556's body-vs-handle distinction no
           * longer has a surface, so cross-stage-by-accident under Rank sort —
           * what that guard existed to prevent — is possible again. That is
           * the accepted cost of "the whole card is the drag area".
           */
          <Box
            testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_RANK_DRAG_HANDLE}
            accessibilityLabel="Drag to reorder rank or move stage"
            className="min-w-0 flex-1"
          >
            {renderCardBase(props)}
          </Box>
        ) : (
          renderCardBase(props)
        )}
      </UIBox>
    </Motion.View>

    <UnifiedTagModal
      isOpen={props.showTagSelector}
      mode="select"
      onClose={props.onCloseTagSelector}
      playerId={props.playerId}
      playerTagIds={props.currentTagIds}
      boardId={props.boardId}
      onTagsUpdated={props.onTagsChanged}
    />
  </>
);

KanbanPlayerCardView.displayName = "KanbanPlayerCardView";

export default memo(KanbanPlayerCardView);
